import { Children, Fragment, isValidElement, useId, useLayoutEffect, useRef, useState, type ComponentProps, type ReactNode, type SelectHTMLAttributes } from "react";
import { Select, type SelectOption } from "./Select";

function text(node: ReactNode): string {
  return Children.toArray(node).map((child) => isValidElement<{ children?: ReactNode }>(child) ? text(child.props.children) : String(child)).join("");
}

function choices(children: ReactNode, group?: string, groupDisabled = false): SelectOption[] {
  return Children.toArray(children).flatMap((child) => {
    if (!isValidElement<{ children?: ReactNode; value?: string | number; label?: string; disabled?: boolean }>(child)) return [];
    if (child.type === Fragment) return choices(child.props.children, group, groupDisabled);
    if (child.type === "optgroup") return choices(child.props.children, child.props.label, groupDisabled || Boolean(child.props.disabled));
    if (child.type !== "option") return [];
    const label = child.props.label ?? text(child.props.children);
    return [{ value: String(child.props.value ?? text(child.props.children)), label, description: group, disabled: groupDisabled || child.props.disabled }];
  });
}

/** Native form semantics, rendered through the same Celia picker as project/role controls. */
export function FormSelect({ children, className = "", id, renderOption, renderPreview, ...props }: SelectHTMLAttributes<HTMLSelectElement> & {
  "data-testid"?: string; renderOption?: ComponentProps<typeof Select>["renderOption"];
  renderPreview?: ComponentProps<typeof Select>["renderPreview"];
}) {
  const uid = useId();
  const triggerId = id ?? `${uid}-choice`;
  const native = useRef<HTMLSelectElement>(null);
  const wrapper = useRef<HTMLSpanElement>(null);
  const options = choices(children);
  const [current, setCurrent] = useState(String(props.value ?? props.defaultValue ?? options.find((o) => !o.disabled)?.value ?? ""));
  const [label, setLabel] = useState(props["aria-label"] ?? props.name ?? "Choose an option");

  useLayoutEffect(() => {
    if (native.current) setCurrent(native.current.value);
    const container = wrapper.current;
    const parent = container?.closest("label");
    const external = id ? document.querySelector(`label[for="${CSS.escape(id)}"]`) : null;
    const source = external ?? parent;
    if (!props["aria-label"] && source) {
      const copy = source.cloneNode(true) as HTMLElement;
      copy.querySelectorAll(".celia-form-select, input, button, select").forEach((e) => e.remove());
      const name = copy.textContent?.trim();
      if (name) setLabel(name);
    }
  });
  useLayoutEffect(() => {
    const form = native.current?.form;
    const reset = () => { queueMicrotask(() => { if (native.current) setCurrent(native.current.value); }); };
    form?.addEventListener("reset", reset);
    return () => form?.removeEventListener("reset", reset);
  }, []);

  return <span ref={wrapper} className={`celia-form-select ${className}`} style={props.style}>
    <select {...props} id={`${triggerId}-native`} ref={native} className="celia-select-native" tabIndex={-1}
      aria-hidden="true" data-testid={undefined}
      onFocus={() => document.getElementById(triggerId)?.focus()}
      onChange={(event) => { setCurrent(event.currentTarget.value); props.onChange?.(event); }}>
      {children}
    </select>
    <Select id={triggerId} label={label} value={props.value === undefined ? current : String(props.value)}
      options={options} renderOption={renderOption} renderPreview={renderPreview} disabled={props.disabled} size="form" searchable={options.length > 8}
      testId={props["data-testid"]} describedBy={props["aria-describedby"]} invalid={props["aria-invalid"]} required={props.required}
      onChange={(value) => {
        const element = native.current;
        if (!element) return;
        element.value = value;
        element.dispatchEvent(new Event("change", { bubbles: true }));
      }} />
  </span>;
}
